import { useEffect, useState } from "react";
import { faRepeatAlt } from "@fortawesome/pro-regular-svg-icons";
import { useStripe } from "@stripe/react-stripe-js";

import { captureException } from "common/utils/monitoring";
import { toDollars } from "common/utils/formatting";
import { SubscriptionPriceSummary } from "common/types/priceSummary";
import { toDate } from "common/utils/dates";
import { Feature, PaymentMethodOption, UISubscriptionStatus, WalletType } from "common/types";
import { useNavigate, useSearchState } from "common/router/hooks";
import { OnboardingClientEventKind } from "common/types/onboarding";
import Icon from "common/components/Icon";
import { Optional } from "common/utils/types";
import { deepCombineErrors } from "common/utils/errors";
import { FetchError } from "common/fetch/types";

import {
  IOnboardingSubscription,
  OnboardingSubscriptionsBillingStepParams,
  OnboardingSubscriptionsStep,
  OnboardingSubscriptionsStepParams,
} from "client/onboarding/types";
import Button from "client/common/components/buttons/Button";
import { ModalStatus } from "client/common/types";
import TextChip from "client/common/components/content/TextChip";
import { IClient } from "client/accounts/types";
import { useOnboardingEventsMutation } from "client/onboarding/hooks";
import PaymentMethodSelectPanel from "client/payments/components/PaymentMethodSelectPanel";
import { useUpdateClientMutation } from "client/accounts/hooks";
import { useFeatures } from "client/clinics/hooks";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";
import Alert from "client/common/components/content/Alert";
import TextButton from "client/common/components/buttons/TextButton";
import AddressSelectPanel from "client/accounts/components/AddressSelectPanel";
import PriceSummary from "client/common/components/display/PriceSummary";
import { Paths, urls } from "client/routes";
import { useCreateAddressDrawer } from "client/accounts/stackables";
import { usePaymentCreateDrawer } from "client/payments/stackables";
import {
  useCreatePaymentMethodMutation,
  useWalletPaymentMethodRequest,
} from "client/payments/hooks";
import { useUpdateBulkSubscriptionsMutation } from "client/subscriptions/hooks";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";

import StepLayout from "../OnboardingSubscriptionsStepLayout";
import OnboardingSubscriptionItem from "../OnboardingSubscriptionItem";

import styles from "../../styles/OnboardingSubscriptionsBillingStep.module.less";

type OnboardingSubscriptionSummaryProps = {
  subscription: IOnboardingSubscription;
};

const OnboardingSubscriptionSummary = ({ subscription }: OnboardingSubscriptionSummaryProps) => {
  const navigate = useNavigate();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];

  const getChargeText = () => {
    if (!isEdbEnabled || subscription.next_order_date) {
      return subscription.next_order_date
        ? `You will not be charged until ${toDate(subscription.next_order_date)}`
        : "You will not be charged until your order is placed";
    }
    return subscription.expected_delivery_by
      ? `You won't be charged until your order is placed — estimated to arrive ${toDate(
          subscription.expected_delivery_by,
        )}`
      : "You won't be charged until your order is placed";
  };

  return (
    <OnboardingSubscriptionItem
      subscription={subscription}
      imageSize="s"
      renderSideContent={() => (
        <div className={styles.itemDetails}>
          <div className={styles.subtotalValue}>{toDollars(subscription.unit_price)}</div>
          <div className={styles.itemLinks}>
            <TextButton
              type="button"
              className={styles.link}
              onClick={() =>
                navigate(
                  urls.onboarding.subscriptions.detail({
                    params: {
                      id: subscription.id,
                      step: OnboardingSubscriptionsStep.QuantityFrequency,
                    },
                  }),
                )
              }
            >
              Edit
            </TextButton>
            <TextButton
              type="button"
              className={styles.link}
              onClick={() =>
                navigate(
                  urls.onboarding.subscriptions.detail({
                    params: {
                      id: subscription.id,
                      step: OnboardingSubscriptionsStep.Cancel,
                    },
                  }),
                )
              }
            >
              Cancel
            </TextButton>
          </div>
        </div>
      )}
      renderProductContent={() => (
        <TextChip size="s" variant="promotion">
          <Icon icon={faRepeatAlt} />
          <>Ships {subscription.frequency_display.toLocaleLowerCase()}</>
        </TextChip>
      )}
    >
      <div className={styles.itemBody}>
        <PriceSummary<SubscriptionPriceSummary>
          data={{
            ...subscription,
            discount: subscription.first_discount_value,
            total: subscription.total,
            tax: null,
            is_tax_final: false,
            total_discount_label:
              subscription.first_discount_value === subscription.recurring_discount_value
                ? "Autoship discount"
                : "First time Autoship discount",
            total_label: "Estimated total",
          }}
          styles={{
            rows: styles.subtotals,
            row: styles.subtotal,
            total: styles.total,
            label: styles.subtotalLabel,
            value: styles.subtotalValue,
          }}
        />
        <Alert>{getChargeText()}</Alert>
      </div>
    </OnboardingSubscriptionItem>
  );
};

type SetModalVisibility = (
  param: keyof OnboardingSubscriptionsBillingStepParams,
  visibility: ModalStatus,
) => void;

type OnboardingSubscriptionProps = {
  client: IClient;
  onSetModalVisibility: SetModalVisibility;
};

const OnboardingSubscriptionAddress = ({
  client,
  onSetModalVisibility,
}: OnboardingSubscriptionProps) => {
  const invalidateOnboarding = useInvalidateOnboarding();
  const {
    mutate: updateClient,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateClientMutation({
    onSuccess: () => {
      invalidateOnboarding();
    },
  });

  return (
    <div className={styles.address}>
      <div className={styles.label}>Shipping Address</div>
      <AddressSelectPanel
        value={client.primary_address}
        options={client.addresses}
        onClick={(address) => updateClient({ primary_address: address.id })}
        onAdd={() => onSetModalVisibility("createAddressModalIsVisible", ModalStatus.Open)}
        disabled={isUpdating}
      />
      {!!updateError && (
        <FetchErrorAlert error={updateError} fieldErrors={[updateError.errors.primary_address]} />
      )}
    </div>
  );
};

type OnboardingSubscriptionPaymentMethodProps = {
  client: IClient;
  createPaymentModalVisible: boolean;
  onSetModalVisibility: SetModalVisibility;
  initialValue: Optional<PaymentMethodOption>;
  value: Optional<PaymentMethodOption>;
  onChange: (method: PaymentMethodOption) => void;
  isLoading: boolean;
  supportedWallets: Optional<Set<WalletType>>;
  updateError: Optional<FetchError>;
};

const OnboardingSubscriptionPaymentMethod = ({
  client,
  createPaymentModalVisible,
  onSetModalVisibility,
  initialValue,
  value,
  onChange,
  isLoading,
  supportedWallets,
  updateError,
}: OnboardingSubscriptionPaymentMethodProps) => {
  const { open: openPaymentCreateDrawer } = usePaymentCreateDrawer();

  const togglePaymentModalVisibility = (visible: boolean) =>
    onSetModalVisibility(
      "createPaymentModalIsVisible",
      visible ? ModalStatus.Open : ModalStatus.Closed,
    );

  useEffect(() => {
    if (createPaymentModalVisible) {
      openPaymentCreateDrawer({
        onSuccess: (method) => onChange(method),
        onClose: () => togglePaymentModalVisibility(false),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createPaymentModalVisible]);

  return (
    <div className={styles.payment}>
      <div className={styles.label}>Payment Method</div>
      <PaymentMethodSelectPanel
        initialValue={initialValue}
        walletPayment={supportedWallets}
        value={value}
        isLoading={isLoading}
        options={client.payment_methods}
        onChange={onChange}
        onAdd={() => togglePaymentModalVisibility(true)}
      />
      {!!updateError && (
        <FetchErrorAlert
          error={updateError}
          fieldErrors={[deepCombineErrors(updateError.errors)]}
        />
      )}
    </div>
  );
};

const OnboardingSubscriptionsBillingStep = ({
  onNext,
  isLoadingNext,
  client,
  stepper,
  onboarding,
}: OnboardingSubscriptionsStepParams) => {
  const stripe = useStripe();
  const invalidateOnboarding = useInvalidateOnboarding();
  const { open: openAddressCreate } = useCreateAddressDrawer();
  const [params, setParams] = useSearchState<Paths["onboarding"]["subscriptions"]["detail"]>({
    createAddressModalIsVisible: ModalStatus.Closed,
    createPaymentModalIsVisible: ModalStatus.Closed,
  });

  const setModalVisibility: SetModalVisibility = (param, visibility) =>
    setParams({ ...params, [param]: visibility });

  useEffect(() => {
    if (params.createAddressModalIsVisible === ModalStatus.Open) {
      openAddressCreate({
        onSuccess: invalidateOnboarding,
        shouldUpdatePrimary: true,
        onClose: () => setModalVisibility("createAddressModalIsVisible", ModalStatus.Closed),
      });
    }
    // generally we only want to trigger this side-effect if the query param changes to open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.createAddressModalIsVisible]);

  const { mutate: createEvent, isLoading: isEventCreating } = useOnboardingEventsMutation({
    onSuccess: invalidateOnboarding,
  });

  const {
    mutate: updateClient,
    isLoading: isUpdatingClient,
    error: updateClientError,
    reset: resetClientUpdate,
  } = useUpdateClientMutation({
    onSuccess: () => {
      createEvent({ kind: OnboardingClientEventKind.LinkSubmitted });
    },
  });

  const {
    mutateAsync: createWalletPaymentMethod,
    isLoading: isCreatingWalletPaymentMethod,
    error: walletPaymentMethodCreateError,
  } = useCreatePaymentMethodMutation();

  const {
    mutate: bulkUpdateSubscriptions,
    isLoading: isBulkUpdatingSubscriptions,
    error: bulkUpdateError,
    reset: resetBulkSubscriptionUpdate,
  } = useUpdateBulkSubscriptionsMutation({
    onError: (error, data) => {
      if (!stripe || !error.data?.stripe_action_client_secret) {
        return;
      }
      stripe.handleCardAction(error.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          bulkUpdateSubscriptions(data);
        }
      });
    },
  });

  const {
    request: paymentRequest,
    isLoading: isPaymentRequestLoading,
    supportedWallets,
  } = useWalletPaymentMethodRequest({
    label: "Choose Payment Method for Recurring Subscriptions",
    amount: onboarding.subscriptions.reduce((total, subscription) => total + subscription.total, 0),
    paymentItems: onboarding.subscriptions.map((subscription) => ({
      label: subscription.next_order_date ? `On ${toDate(subscription.next_order_date)}` : "",
      amount: subscription.total,
    })),
    onCreate: async (id) => {
      // reset the client and bulk update mutations to clear any errors now that we are starting a new flow
      resetClientUpdate();
      resetBulkSubscriptionUpdate();
      const method = await createWalletPaymentMethod({ payment_method: id });
      // we don't want to update all the subscriptions instead of setting the primary payment method
      // in the case that we have a wallet payment method because they are more likely to fail in the future
      // and we don't want to use these as a default value for all future orders/subscriptions.
      bulkUpdateSubscriptions(
        {
          subscription_ids: onboarding.subscriptions.map((subscription) => subscription.id),
          payment_method_id: method.id,
        },
        { onSuccess: onNext },
      );
    },
  });

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<Optional<PaymentMethodOption>>(
    client.primary_payment_method,
  );

  useEffect(() => {
    setSelectedPaymentMethod(client.primary_payment_method);
  }, [client.primary_payment_method]);

  const onConfirm = () => {
    if (!selectedPaymentMethod) {
      return;
    }

    if (selectedPaymentMethod.id === "__wallet__") {
      paymentRequest?.show();
      return;
    }

    if (typeof selectedPaymentMethod.id === "number") {
      updateClient({ primary_payment_method: selectedPaymentMethod.id }, { onSuccess: onNext });
      return;
    }

    captureException(
      new Error(
        `Invalid payment method selected ${JSON.stringify(selectedPaymentMethod)} in Subscription Onboarding Payment step`,
      ),
    );
  };

  const canContinue = selectedPaymentMethod && client.primary_address;
  const isSubmitting =
    isUpdatingClient ||
    isEventCreating ||
    isCreatingWalletPaymentMethod ||
    isBulkUpdatingSubscriptions;
  const updateError = walletPaymentMethodCreateError || updateClientError || bulkUpdateError;

  const cardDeclineError = bulkUpdateError?.data?.card_decline_info;

  return (
    <StepLayout stepper={stepper}>
      <StepLayout.Title>Now we just need your updated billing info...</StepLayout.Title>
      <div className={styles.summaries}>
        {onboarding.subscriptions
          .filter((subscription) => subscription.visible_status === UISubscriptionStatus.Active)
          .map((subscription) => (
            <OnboardingSubscriptionSummary key={subscription.id} subscription={subscription} />
          ))}
        <OnboardingSubscriptionAddress client={client} onSetModalVisibility={setModalVisibility} />
        <OnboardingSubscriptionPaymentMethod
          client={client}
          createPaymentModalVisible={params.createPaymentModalIsVisible === ModalStatus.Open}
          onSetModalVisibility={setModalVisibility}
          initialValue={client.primary_payment_method}
          value={selectedPaymentMethod}
          onChange={setSelectedPaymentMethod}
          isLoading={isPaymentRequestLoading}
          supportedWallets={supportedWallets}
          updateError={cardDeclineError ? null : updateError}
        />
        {cardDeclineError && <CardPaymentDeclineAlert declineError={cardDeclineError} />}
      </div>
      <StepLayout.Footer>
        <Button
          onClick={onConfirm}
          isLoading={isSubmitting || isLoadingNext}
          disabled={!canContinue}
        >
          Confirm
        </Button>
      </StepLayout.Footer>
    </StepLayout>
  );
};

export default OnboardingSubscriptionsBillingStep;
